/**
 * Verify Device Page
 * User lands here when clicking magic link from email
 * Confirms the device and shows animated success/error message
 * Auto-redirects to login after successful verification
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Shield, ArrowRight, Smartphone } from "lucide-react";
import { authApi } from "../../api";
import { translateBackendError } from "../../utils/errors";

export default function VerifyDevice() {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get("token");

    const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
    const [message, setMessage] = useState("");
    const [countdown, setCountdown] = useState(5);

    useEffect(() => {
        const verifyDevice = async () => {
            if (!token) {
                setStatus("error");
                setMessage(t('auth:verifyDevice.tokenMissing'));
                return;
            }

            try {
                await authApi.confirmDevice(token);
                setStatus("success");
                setMessage(t('auth:verifyDevice.successMessage'));
            } catch (err: any) {
                const errorMsg = err.response?.data?.message || err.message || t('auth:verifyDevice.invalidTokenMessage');

                // If device is already verified, treat as success
                if (errorMsg.includes("deja verifye") || errorMsg.includes("already verified")) {
                    setStatus("success");
                    setMessage(t('auth:verifyDevice.alreadyVerifiedMessage'));
                } else {
                    setStatus("error");
                    setMessage(translateBackendError(errorMsg, i18n.language));
                }
            }
        };

        verifyDevice();
    }, [token, t]);

    // Auto-redirect countdown after success
    useEffect(() => {
        if (status !== "success") return;

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    navigate("/login", { replace: true });
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [status, navigate]);

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 sm:p-6 md:p-8">
            <div className="w-full max-w-sm sm:max-w-md">
                {/* Main Card */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    {/* Header */}
                    <div className={`
                        px-6 py-8 sm:px-8 sm:py-10 text-center
                        ${status === "loading" ? "bg-blue-50" : ""}
                        ${status === "success" ? "bg-green-50" : ""}
                        ${status === "error" ? "bg-red-50" : ""}
                    `}>
                        {/* Icon */}
                        <div className={`
                            mx-auto w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center mb-4
                            ${status === "loading" ? "bg-blue-100" : ""}
                            ${status === "success" ? "bg-green-100" : ""}
                            ${status === "error" ? "bg-red-100" : ""}
                        `}>
                            {status === "loading" && (
                                <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600 animate-spin" />
                            )}
                            {status === "success" && (
                                <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-green-600" />
                            )}
                            {status === "error" && (
                                <XCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-600" />
                            )}
                        </div>

                        {/* Title */}
                        <h1 className={`
                            text-xl sm:text-2xl font-bold
                            ${status === "loading" ? "text-blue-900" : ""}
                            ${status === "success" ? "text-green-900" : ""}
                            ${status === "error" ? "text-red-900" : ""}
                        `}>
                            {status === "loading" && t('auth:verifyDevice.verifyingTitle')}
                            {status === "success" && t('auth:verifyDevice.successTitle')}
                            {status === "error" && t('auth:verifyDevice.errorTitle')}
                        </h1>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-6 sm:px-8 sm:py-8">
                        {status === "loading" && (
                            <div className="text-center space-y-4">
                                <Smartphone className="w-12 h-12 mx-auto text-gray-400" />
                                <p className="text-gray-600 text-sm sm:text-base">
                                    {t('auth:verifyDevice.verifyingSubtitle')}
                                </p>
                                <div className="flex justify-center gap-1">
                                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                                    <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                                </div>
                            </div>
                        )}

                        {status === "success" && (
                            <div className="space-y-5">
                                <div className="text-center space-y-2">
                                    <p className="text-gray-800 font-medium text-sm sm:text-base">
                                        {message}
                                    </p>
                                    <p className="text-gray-500 text-xs sm:text-sm">
                                        {t('auth:verifyDevice.redirectMessage')}
                                    </p>
                                </div>

                                {/* Countdown progress */}
                                <div className="space-y-2">
                                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                        <div
                                            className="bg-green-500 h-full rounded-full transition-all duration-1000 ease-linear"
                                            style={{ width: `${(countdown / 5) * 100}%` }}
                                        />
                                    </div>
                                    <p className="text-gray-400 text-xs text-center">
                                        {t('auth:verifyDevice.redirectingIn', { seconds: countdown })}
                                    </p>
                                </div>

                                {/* Manual redirect button */}
                                <button
                                    onClick={() => navigate("/login", { replace: true })}
                                    className="
                                        w-full py-3 px-4 rounded-xl font-semibold text-sm sm:text-base
                                        bg-green-600 hover:bg-green-700
                                        text-white
                                        transition-colors duration-200
                                        flex items-center justify-center gap-2
                                    "
                                >
                                    {t('auth:verifyDevice.loginButton')}
                                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                                </button>
                            </div>
                        )}

                        {status === "error" && (
                            <div className="space-y-5">
                                <div className="text-center space-y-2">
                                    <p className="text-gray-800 text-sm sm:text-base">
                                        {message}
                                    </p>
                                    <p className="text-gray-500 text-xs sm:text-sm">
                                        {t('auth:verifyDevice.tryAgainMessage')}
                                    </p>
                                </div>

                                <button
                                    onClick={() => navigate("/login", { replace: true })}
                                    className="
                                        w-full py-3 px-4 rounded-xl font-semibold text-sm sm:text-base
                                        bg-blue-600 hover:bg-blue-700
                                        text-white
                                        transition-colors duration-200
                                        flex items-center justify-center gap-2
                                    "
                                >
                                    {t('auth:verifyDevice.backToLoginButton')}
                                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer branding */}
                <div className="mt-6 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-400 text-xs sm:text-sm">
                        <Shield className="w-4 h-4" />
                        <span>{t('auth:verifyDevice.securityBranding')}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
